import { useMemo } from 'react';
import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';
import { useOptimisticPreview } from './useOptimisticPreview';
import { readYjsValue } from '../lib/diagram/yjsCompat';

interface UseDiagramWithOptimisticPreviewOptions {
  doc: Y.Doc | null;
  awareness: awarenessProtocol.Awareness | null;
  onShapeUpdate?: (shapeId: string, state: any) => void;
}

export function useDiagramWithOptimisticPreview({ 
  doc, 
  awareness,
  onShapeUpdate
}: UseDiagramWithOptimisticPreviewOptions) {
  
  const {
    isReady,
    updatePreview,
    getShapeState,
    setUserSelection,
    forceCommit,
    manager
  } = useOptimisticPreview({
    ydoc: doc,
    awareness,
  });
  
  // Log awareness state changes for debugging - removed to reduce noise
  
  // Enhanced update function with logging
  const updatePreviewWithLogging = useMemo(() => {
    return (shapeId: string, changes: Record<string, any>) => {
      updatePreview(shapeId, changes);
      
      // Get the merged state and notify
      if (doc) {
        const shapes = doc.getMap('shapes');
        const raw = shapes.get(shapeId);
        if (raw) {
          const baseState = readYjsValue<Record<string, unknown>>(raw);
          const mergedState = getShapeState(shapeId, baseState);
          onShapeUpdate?.(shapeId, mergedState);
        }
      }
    };
  }, [updatePreview, getShapeState, doc, onShapeUpdate]);
  
  // Get user info from awareness
  const userInfo = useMemo(() => {
    if (!awareness) return null;
    const localState = awareness.getLocalState() as any;
    return localState?.user || null;
  }, [awareness]);
  
  return {
    isReady,
    updatePreview: updatePreviewWithLogging,
    getShapeState,
    setUserSelection,
    forceCommit,
    manager,
    userInfo,
    awareness
  };
}